import React, { useState, useEffect, useContext } from 'react';
import { createComment, getComments, getReplies, updateComment, deleteComment, toggleLike } from '../../api';
import { UserContext } from '../../context/UserContext';
import HrnbCard from '../controls/HrnbCard';
import HrnbButton from '../controls/HrnbButton';
import HrnbTextArea from '../controls/HrnbTextArea';
import HrnbAlert from '../controls/HrnbAlert';
import HrnbLoader from '../controls/HrnbLoader';
import HrnbModal from '../controls/HrnbModal';
import HrnbFormControl from '../controls/HrnbFormControl';
import HrnbLabel from '../controls/HrnbLabel';
import '../../css/duels/DuelSpectatorComments.css';

/**
 * Компонент комментариев зрителей для дуэли
 */
function DuelSpectatorComments({ duelId }) {
    const { currentUser } = useContext(UserContext);
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [newComment, setNewComment] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [replyTo, setReplyTo] = useState(null);
    const [replyContent, setReplyContent] = useState('');
    const [editingComment, setEditingComment] = useState(null);
    const [editContent, setEditContent] = useState('');
    const [showReplies, setShowReplies] = useState({});

    const size = 20;

    // Загрузка комментариев
    useEffect(() => {
        loadComments();
    }, [duelId, page]);

    const loadComments = async () => {
        setLoading(true);
        setError(null);

        try {
            const data = await getComments(duelId, { page, size });
            setComments(data.comments || []);
            setTotalPages(data.totalPages || 0);
            setTotalElements(data.totalElements || 0);
        } catch (err) {
            console.error('[DuelSpectatorComments] Failed to load comments:', err);
            setError('Не удалось загрузить комментарии');
        } finally {
            setLoading(false);
        }
    };

    // Отправка нового комментария
    const handleSubmitComment = async (e) => {
        e.preventDefault();

        if (!newComment.trim() && !replyContent.trim()) {
            setError('Введите текст комментария');
            return;
        }

        setSubmitting(true);
        setError(null);

        try {
            const content = replyContent.trim() || newComment.trim();
            const parentCommentId = replyTo ? replyTo.id : null;

            await createComment(duelId, { content, parentCommentId });
            setSuccess(replyTo ? 'Ответ отправлен' : 'Комментарий отправлен');
            
            if (replyTo) {
                setReplyTo(null);
                setReplyContent('');
            } else {
                setNewComment('');
            }

            loadComments();
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            console.error('[DuelSpectatorComments] Failed to submit comment:', err);
            setError(err.response?.data?.message || 'Не удалось отправить комментарий');
        } finally {
            setSubmitting(false);
        }
    };

    // Лайк комментария
    const handleLike = async (commentId) => {
        if (!currentUser) {
            setError('Необходимо войти для лайков');
            return;
        }

        try {
            await toggleLike(duelId, commentId);
            loadComments();
        } catch (err) {
            console.error('[DuelSpectatorComments] Failed to like comment:', err);
            setError('Не удалось поставить лайк');
        }
    };

    // Удаление комментария
    const handleDelete = async (commentId) => {
        if (!window.confirm('Удалить комментарий?')) return;

        try {
            await deleteComment(duelId, commentId);
            setSuccess('Комментарий удалён');
            loadComments();
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            console.error('[DuelSpectatorComments] Failed to delete comment:', err);
            setError('Не удалось удалить комментарий');
        }
    };

    // Редактирование комментария
    const handleEdit = (comment) => {
        setEditingComment(comment);
        setEditContent(comment.content);
    };

    const handleSaveEdit = async () => {
        if (!editContent.trim()) {
            setError('Введите текст комментария');
            return;
        }

        try {
            await updateComment(duelId, editingComment.id, { content: editContent });
            setSuccess('Комментарий обновлён');
            setEditingComment(null);
            loadComments();
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            console.error('[DuelSpectatorComments] Failed to update comment:', err);
            setError('Не удалось обновить комментарий');
        }
    };

    // Загрузка ответов
    const loadReplies = async (commentId) => {
        try {
            const data = await getReplies(duelId, commentId, { page: 0, size: 10 });
            setShowReplies(prev => ({ ...prev, [commentId]: data.comments || [] }));
        } catch (err) {
            console.error('[DuelSpectatorComments] Failed to load replies:', err);
        }
    };

    const toggleReplies = (commentId) => {
        if (showReplies[commentId]) {
            setShowReplies(prev => ({ ...prev, [commentId]: null }));
        } else {
            loadReplies(commentId);
        }
    };

    if (loading && comments.length === 0) {
        return <HrnbLoader />;
    }

    const hasParent = (comment) => comment.parentCommentId !== null;

    return (
        <div className="duel-spectator-comments">
            {error && (
                <HrnbAlert variant="danger" onClose={() => setError(null)}>
                    {error}
                </HrnbAlert>
            )}

            {success && (
                <HrnbAlert variant="success" onClose={() => setSuccess(null)}>
                    {success}
                </HrnbAlert>
            )}

            {/* Форма нового комментария */}
            {currentUser ? (
                <form onSubmit={handleSubmitComment} className="duel-spectator-comments__form">
                    <HrnbFormControl>
                        <HrnbLabel>
                            {replyTo ? `Ответ ${replyTo.authorName}` : 'Новый комментарий'}
                        </HrnbLabel>
                        <HrnbTextArea
                            value={replyTo ? replyContent : newComment}
                            onChange={(e) => replyTo ? setReplyContent(e.target.value) : setNewComment(e.target.value)}
                            placeholder={replyTo ? 'Ваш ответ...' : 'Напишите комментарий...'}
                            rows={replyTo ? 3 : 4}
                            disabled={submitting}
                        />
                    </HrnbFormControl>

                    <div className="duel-spectator-comments__form-actions">
                        {replyTo && (
                            <HrnbButton
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    setReplyTo(null);
                                    setReplyContent('');
                                }}
                            >
                                Отмена ответ
                            </HrnbButton>
                        )}
                        <HrnbButton
                            type="submit"
                            variant="primary"
                            disabled={submitting || (!newComment.trim() && !replyContent.trim())}
                        >
                            {submitting ? 'Отправка...' : (replyTo ? 'Ответить' : 'Отправить')}
                        </HrnbButton>
                    </div>
                </form>
            ) : (
                <div className="duel-spectator-comments__auth-required">
                    <p>🔐 Войдите, чтобы комментировать</p>
                    <a href="/login">Войти</a>
                </div>
            )}

            {/* Список комментариев */}
            <div className="duel-spectator-comments__list">
                {comments.length === 0 ? (
                    <div className="duel-spectator-comments__empty">
                        <p>Комментариев пока нет. Будьте первым!</p>
                    </div>
                ) : (
                    <>
                        {comments.filter(c => !hasParent(c)).map((comment) => (
                            <div key={comment.id} className="duel-spectator-comments__comment">
                                <div className="duel-spectator-comments__comment-header">
                                    <div className="duel-spectator-comments__author">
                                        <span className="duel-spectator-comments__author-name">
                                            {comment.authorName}
                                        </span>
                                        {comment.authorId === currentUser?.id && (
                                            <span className="duel-spectator-comments__author-badge">Вы</span>
                                        )}
                                    </div>
                                    <span className="duel-spectator-comments__date">
                                        {new Date(comment.createdAt).toLocaleString('ru-RU')}
                                    </span>
                                </div>

                                <div className="duel-spectator-comments__content">
                                    {editingComment?.id === comment.id ? (
                                        <div className="duel-spectator-comments__edit">
                                            <HrnbTextArea
                                                value={editContent}
                                                onChange={(e) => setEditContent(e.target.value)}
                                                rows={3}
                                            />
                                            <div className="duel-spectator-comments__edit-actions">
                                                <HrnbButton
                                                    variant="primary"
                                                    size="sm"
                                                    onClick={handleSaveEdit}
                                                >
                                                    Сохранить
                                                </HrnbButton>
                                                <HrnbButton
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => setEditingComment(null)}
                                                >
                                                    Отмена
                                                </HrnbButton>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <p>{comment.content}</p>
                                            {comment.isEdited && (
                                                <span className="duel-spectator-comments__edited">
                                                    (ред.)
                                                </span>
                                            )}
                                        </>
                                    )}
                                </div>

                                <div className="duel-spectator-comments__actions">
                                    <button
                                        className={`duel-spectator-comments__action duel-spectator-comments__like ${comment.isLikedByMe ? 'duel-spectator-comments__like--active' : ''}`}
                                        onClick={() => handleLike(comment.id)}
                                        title="Лайк"
                                    >
                                        ❤️ {comment.likeCount || 0}
                                    </button>

                                    <button
                                        className="duel-spectator-comments__action"
                                        onClick={() => setReplyTo(comment)}
                                        title="Ответить"
                                    >
                                        💬 Ответить
                                    </button>

                                    {comment.repliesCount > 0 && (
                                        <button
                                            className="duel-spectator-comments__action"
                                            onClick={() => toggleReplies(comment.id)}
                                            title="Показать ответы"
                                        >
                                            {showReplies[comment.id] ? '🔼' : '🔽'} {comment.repliesCount}
                                        </button>
                                    )}

                                    {comment.authorId === currentUser?.id && (
                                        <>
                                            <button
                                                className="duel-spectator-comments__action"
                                                onClick={() => handleEdit(comment)}
                                                title="Редактировать"
                                            >
                                                ✏️
                                            </button>
                                            <button
                                                className="duel-spectator-comments__action"
                                                onClick={() => handleDelete(comment.id)}
                                                title="Удалить"
                                            >
                                                🗑️
                                            </button>
                                        </>
                                    )}
                                </div>

                                {/* Ответы */}
                                {showReplies[comment.id] && (
                                    <div className="duel-spectator-comments__replies">
                                        {showReplies[comment.id].map((reply) => (
                                            <div key={reply.id} className="duel-spectator-comments__reply">
                                                <div className="duel-spectator-comments__reply-header">
                                                    <span className="duel-spectator-comments__reply-author">
                                                        {reply.authorName}
                                                    </span>
                                                    <span className="duel-spectator-comments__reply-date">
                                                        {new Date(reply.createdAt).toLocaleString('ru-RU')}
                                                    </span>
                                                </div>
                                                <p className="duel-spectator-comments__reply-content">
                                                    {reply.content}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}

                        {/* Пагинация */}
                        {totalPages > 1 && (
                            <div className="duel-spectator-comments__pagination">
                                <HrnbButton
                                    variant="secondary"
                                    disabled={page === 0}
                                    onClick={() => setPage(page - 1)}
                                >
                                    Назад
                                </HrnbButton>
                                <span>
                                    Страница {page + 1} из {totalPages} ({totalElements})
                                </span>
                                <HrnbButton
                                    variant="secondary"
                                    disabled={page >= totalPages - 1}
                                    onClick={() => setPage(page + 1)}
                                >
                                    Вперёд
                                </HrnbButton>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

export default DuelSpectatorComments;
